// src/pages/SixDetail/SixDetailView.tsx
import React, { useState } from 'react';
import { DndContext, DragOverlay } from '@dnd-kit/core';
import type { CollisionDetection, DragStartEvent, DragEndEvent, SensorDescriptor, SensorOptions } from '@dnd-kit/core';
import { AlertCircle, Clock, Files as FilesIcon, Loader2, Users, Wifi, WifiOff } from 'lucide-react';
import {
  BulkActionsBar,
  Tab,
  Tabs,
} from 'shared-portal-components-v2';

import DragMovePreview from '@/components/organism/SixDetail/dnd/DragMovePreview';
import DroppableFolderBreadcrumb from '@/components/organism/SixDetail/dnd/DroppableFolderBreadcrumb';
import MouseTrackingOverlay from '@/components/organism/SixDetail/dnd/MouseTrackingOverlay';
import type {
  DownloadFilterKey,
  FileEntry,
  FileSortDir,
  FileSortKey,
  FolderBreadcrumbItem,
  ParticipantCardModel,
} from '@/types/SixDetail.types';
import type { UploadProgress } from '@/types/poc/helia';
import { classNames } from '@/utils/classNames.utils';
import type { SixDetailActivityItemModel } from '@/components/organism/SixDetail/SixDetailActivityPanel/SixDetailActivityPanel';
import SixDetailActivityPanel from '@/components/organism/SixDetail/SixDetailActivityPanel/SixDetailActivityPanel';
import SixDetailFileBrowser from '@/components/organism/SixDetail/SixDetailFileBrowser';
import SixDetailParticipantsPanel from '@/components/organism/SixDetail/SixDetailParticipantsPanel/SixDetailParticipantsPanel';
import type { ConnectionStatus } from '@/store/slices/poc/connectionSlice';

type DetailTabKey = 'files' | 'participants' | 'activity';

const CONNECTION_BADGE: Record<
  ConnectionStatus,
  { label: string; className: string; icon: React.ReactElement }
> = {
  connected: {
    label: 'Connected',
    className: 'text-emerald-600 bg-emerald-50 border-emerald-200',
    icon: <Wifi className="h-3 w-3" />,
  },
  connecting: {
    label: 'Connecting',
    className: 'text-amber-600 bg-amber-50 border-amber-200',
    icon: <Loader2 className="h-3 w-3 animate-spin" />,
  },
  disconnected: {
    label: 'Disconnected',
    className: 'text-muted-foreground bg-muted/50 border-border',
    icon: <WifiOff className="h-3 w-3" />,
  },
  error: {
    label: 'Error',
    className: 'text-red-600 bg-red-50 border-red-200',
    icon: <AlertCircle className="h-3 w-3" />,
  },
};

export interface SixDetailViewProps {
  sixName: string;
  sixDescription: string;
  connectionStatus: ConnectionStatus;
  files: FileEntry[];
  showPathColumn: boolean;
  totalFilesCount: number;
  canManageAdministrativeActions: boolean;
  searchQuery?: string;
  downloadFilterKey: DownloadFilterKey;
  selectedFileIds: string[];
  sortKey: FileSortKey;
  sortDir: FileSortDir;

  membersCount: number;
  lastActivityLabel: string;
  participantCards: ParticipantCardModel[];
  activityCards: SixDetailActivityItemModel[];
  breadcrumbItems: Array<FolderBreadcrumbItem & { onClick?: () => void }>;
  inFlightUploads: UploadProgress[];
  showInlineUploads: boolean;

  onNavigateDashboard: () => void;
  onSearchChange: (query: string) => void;
  onDownloadFilterChange: (filterKey: DownloadFilterKey) => void;
  onSelectionChange: (ids: string[]) => void;
  onClearSelection: () => void;
  onSortChange: (key: FileSortKey, dir: FileSortDir) => void;
  onBulkDownload: () => void;
  onBulkDelete: () => void;
  onItemAction: (item: FileEntry, action: string) => void;
  canShowRowActions: (item: FileEntry) => boolean;
  onUploadProgressComplete: (uploadId: string) => void;

  dndSensors: Array<SensorDescriptor<SensorOptions>>;
  dndCollisionDetection: CollisionDetection;
  dndActiveItem?: FileEntry;
  dndActiveDragCount: number;
  isDragDisabled: boolean;
  onDragStart: (event: DragStartEvent) => void;
  onDragEnd: (event: DragEndEvent) => void;
  onDragCancel: () => void;

  onManageAccessClick?: () => void;
  onExportActivity: () => void;
}

function SixDetailView({
  sixName,
  sixDescription,
  connectionStatus,
  files,
  showPathColumn,
  totalFilesCount,
  canManageAdministrativeActions,
  searchQuery,
  downloadFilterKey,
  selectedFileIds,
  sortKey,
  sortDir,
  membersCount,
  lastActivityLabel,
  participantCards,
  activityCards,
  breadcrumbItems,
  inFlightUploads,
  showInlineUploads,
  onNavigateDashboard,
  onSearchChange,
  onDownloadFilterChange,
  onSelectionChange,
  onClearSelection,
  onSortChange,
  onBulkDownload,
  onBulkDelete,
  onItemAction,
  canShowRowActions,
  onUploadProgressComplete,
  dndSensors,
  dndCollisionDetection,
  dndActiveItem,
  dndActiveDragCount,
  isDragDisabled,
  onDragStart,
  onDragEnd,
  onDragCancel,
  onManageAccessClick,
  onExportActivity,
}: SixDetailViewProps): React.ReactElement {
  const [activeTabKey, setActiveTabKey] = useState<DetailTabKey>('files');

  function handleManageAccessClick(): void {
    if (typeof onManageAccessClick === 'function') onManageAccessClick();
  }

  function handleSelectFilesTab(): void {
    setActiveTabKey('files');
  }

  function handleSelectParticipantsTab(): void {
    setActiveTabKey('participants');
  }

  function handleSelectActivityTab(): void {
    setActiveTabKey('activity');
  }

  const safeDownloadFilterKey: DownloadFilterKey =
    downloadFilterKey === 'downloaded' || downloadFilterKey === 'notDownloaded'
      ? downloadFilterKey
      : 'allFiles';

  return (
    <DndContext
      sensors={dndSensors}
      collisionDetection={dndCollisionDetection}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragCancel={onDragCancel}
    >
    <div className="flex-1 flex flex-col">
      <div className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <DroppableFolderBreadcrumb items={breadcrumbItems} onNavigateRoot={onNavigateDashboard} />

        <div className="p-3 sm:p-6 pb-4">
          <div className="mb-4">
            <div className="flex items-center gap-2.5 mb-1">
              <h1 className="text-xl sm:text-2xl font-bold text-foreground">{sixName}</h1>
              <span className={classNames(
                'inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium',
                CONNECTION_BADGE[connectionStatus].className,
              )}>
                {CONNECTION_BADGE[connectionStatus].icon}
                {CONNECTION_BADGE[connectionStatus].label}
              </span>
            </div>
            <p className="text-sm sm:text-base text-muted-foreground">
              {sixDescription}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3 mb-3">
            <div className="flex items-center p-2 sm:p-3 rounded-lg bg-muted/30 border">
              <Users className="h-4 w-4 text-primary mr-2 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground">{membersCount}</p>
                <p className="text-xs text-muted-foreground">Members</p>
              </div>
            </div>

            <div className="flex items-center p-2 sm:p-3 rounded-lg bg-muted/30 border">
              <FilesIcon className="h-4 w-4 text-primary mr-2 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground">{totalFilesCount}</p>
                <p className="text-xs text-muted-foreground">Files</p>
              </div>
            </div>

            <div className="flex items-center p-2 sm:p-3 rounded-lg bg-muted/30 border col-span-full sm:col-span-1">
              <Clock className="h-4 w-4 text-primary mr-2 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-xs sm:text-sm font-medium text-foreground truncate">
                  {lastActivityLabel}
                </p>
                <p className="text-xs text-muted-foreground">Last Activity</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-hidden">
        <div className="h-full flex flex-col">
          <div className="border-b border-border bg-background px-3 sm:px-6">
            <Tabs className="border-b-0 w-full sm:w-auto overflow-x-auto">
              <Tab
                label="Files"
                isActive={activeTabKey === 'files'}
                onClick={handleSelectFilesTab}
                className="h-12 px-3 sm:px-6 py-3 flex-shrink-0"
              />
              <Tab
                label="Participants"
                isActive={activeTabKey === 'participants'}
                onClick={handleSelectParticipantsTab}
                className="h-12 px-3 sm:px-6 py-3 flex-shrink-0"
              />
              <Tab
                label="Activity"
                isActive={activeTabKey === 'activity'}
                onClick={handleSelectActivityTab}
                className="h-12 px-3 sm:px-6 py-3 flex-shrink-0"
              />
            </Tabs>
          </div>

          <div className="flex-1 overflow-auto">
            {activeTabKey === 'files' && (
              <div className="h-full flex flex-col">
                <div className="flex-1 p-4 sm:p-6 space-y-4 sm:space-y-6 overflow-auto">
                  <BulkActionsBar
                    selectedCount={selectedFileIds.length}
                    onDownload={onBulkDownload}
                    onDelete={onBulkDelete}
                    onClearSelection={onClearSelection}
                  />

                  <SixDetailFileBrowser
                    items={files}
                    showPathColumn={showPathColumn}
                    uploads={inFlightUploads}
                    showUploads={showInlineUploads}
                    searchQuery={searchQuery ?? ''}
                    onSearchChange={onSearchChange}
                    downloadFilterKey={safeDownloadFilterKey}
                    onDownloadFilterChange={onDownloadFilterChange}
                    sortKey={sortKey}
                    sortDir={sortDir}
                    onSortChange={onSortChange}
                    selectedIds={selectedFileIds}
                    onSelectionChange={onSelectionChange}
                    onItemAction={onItemAction}
                    canShowRowActions={canShowRowActions}
                    onUploadProgressComplete={onUploadProgressComplete}
                    isDragDisabled={isDragDisabled}
                    searchPlaceholder="Search files..."
                  />
                </div>
              </div>
            )}

            {activeTabKey === 'participants' && (
              <SixDetailParticipantsPanel
                className="p-3 sm:p-6"
                membersCount={membersCount}
                participantCards={participantCards}
                onManageAccessClick={canManageAdministrativeActions ? handleManageAccessClick : undefined}
              />
            )}

            {activeTabKey === 'activity' && (
              <SixDetailActivityPanel
                className="p-3 sm:p-6"
                activityCards={activityCards}
                onExport={canManageAdministrativeActions ? onExportActivity : undefined}
              />
            )}
          </div>
        </div>
      </div>
    </div>

    <DragOverlay dropAnimation={null} style={{ opacity: 0, pointerEvents: 'none' }} />
    {dndActiveItem !== undefined && (
      <MouseTrackingOverlay>
        <DragMovePreview item={dndActiveItem} totalCount={dndActiveDragCount} />
      </MouseTrackingOverlay>
    )}
    </DndContext>
  );
}

export default SixDetailView;
